import DeleteModal from "components/DeleteModal";
import { useTooltip } from "context/TooltipProvider";
import imageVector from "images/imageVector.png";
import SuspectDetailInfo from "pages/Incident/IncidentDetail/modals/SuspectDetailInfo";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { deleteCaseDefendantLink } from "services/caseServices";
import {
  CaseCreation,
  CaseDefendantLink,
  FILE_URL,
  Reporter,
  Suspect,
  Victim,
  Witness,
} from "types";
import { constructFileUrl, errorHandler } from "utils/helper";
import DefendantLinkForm from "./DefendantLinkForm";
import SuspectClosureForm from "./SuspectClosureForm";

type Props = {
  caseData: CaseCreation;
  caseRelatedParties: {
    suspects: Suspect[];
    witnesses: Witness[];
    victims: Victim[];
    reporters: Reporter[];
  };
  fetchData: () => void;
  isCoordinator?: boolean;
  isRegistrar?: boolean;
  isProsecutor?: boolean;
};

const DefendantList: FC<Props> = ({
  caseData,
  caseRelatedParties,
  fetchData,
  isCoordinator = false,
  isRegistrar = false,
  isProsecutor = false,
}) => {
  const { t } = useTranslation();
  const { showTooltip } = useTooltip();
  const [openAccordion, setOpenAccordion] = useState<number | null>(0);
  const [selectedDefendant, setSelectedDefendant] = useState<Suspect>();
  const [isOpenDefendantDetail, setIsOpenDefendantDetail] = useState(false);
  const [openForm, setOpenForm] = useState(false);
  const [openClosureForm, setOpenClosureForm] = useState(false);
  const [isOpenDelete, setOpenDelete] = useState(false);
  const [selectedData, setSelectedData] = useState<CaseDefendantLink>();
  const [action, setAction] = useState<"add" | "edit">("add");

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-2 h-8 bg-red-600 rounded-full"></div>
            <h3 className="text-lg font-semibold text-gray-900">Defendants</h3>
          </div>

          {isProsecutor &&
            (caseData?.status === "Pending" ||
              caseData?.status === "Respond_Back_prosecutor") && (
              <div className="flex flex-row gap-2">
                <button
                  type="button"
                  className="px-6 py-3 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors"
                  onClick={() => {
                    setOpenForm(true);
                    setAction("add");
                  }}
                >
                  + {t("add")} {t("ioc_defendant_label")}
                </button>

                <button
                  type="button"
                  className="px-6 py-3 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors"
                  onClick={() => {
                    setOpenClosureForm(true);
                    setAction("add");
                  }}
                >
                  + {t("ioc_add_closure")}
                </button>
              </div>
            )}
        </div>

        {caseData?.case_defendant_links &&
        caseData.case_defendant_links.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {t("ioc_no_defendant_case_details")}
          </div>
        ) : (
          <div className="space-y-4">
            {caseData?.case_defendant_links?.map(
              (caseLink: CaseDefendantLink, index: number) => {
                const defendant: any = caseLink?.defendant_info;
                const relatedCounts = caseData?.counts?.filter((count: any) =>
                  count?.defendants_info?.some(
                    (def: any) =>
                      def?.defendant_info?.uu_id === defendant?.uu_id,
                  ),
                );
                const relatedWitnesses = caseData?.witnesses?.filter((w: any) =>
                  w?.witnessed_defendants_info?.some(
                    (def: any) =>
                      def?.defendant_info?.uu_id === defendant?.uu_id,
                  ),
                );
                const isOpen = openAccordion === index;

                return (
                  <div
                    key={index}
                    className="border border-gray-200 rounded-lg overflow-hidden"
                  >
                    <button
                      className="w-full flex justify-between items-center px-6 py-4 bg-gray-50 hover:bg-gray-100 transition-colors"
                      onClick={() =>
                        setOpenAccordion((prev) =>
                          prev === index ? null : index,
                        )
                      }
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                          <span className="font-semibold text-blue-600">
                            {caseLink?.defendant_number}
                          </span>
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold text-gray-900">
                            {defendant?.first_name} {defendant?.middle_name}{" "}
                            {defendant?.last_name}
                          </h3>
                          <div className="text-xs text-gray-500">
                            {t("ioc_defendant_number", {
                              number: caseLink?.defendant_number,
                            })}
                          </div>
                        </div>
                      </div>
                      <svg
                        className={`w-5 h-5 text-gray-500 transition-transform ${
                          isOpen ? "rotate-180" : ""
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>

                    {isOpen && (
                      <div className="p-6 space-y-6">
                        <div className="flex items-center gap-4">
                          <img
                            src={(() => {
                              const doc = defendant?.documents?.find(
                                (doc: any) =>
                                  doc.document_type === "suspect_picture",
                              );
                              return doc
                                ? constructFileUrl(doc, FILE_URL.SUSPECT)
                                : imageVector;
                            })()}
                            alt={t("ioc_defendant_label")}
                            className="w-16 h-16 rounded-full"
                          />
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900">
                              {defendant?.first_name} {defendant?.middle_name}{" "}
                              {defendant?.last_name}
                            </h4>
                            <div className="text-sm text-gray-600 mt-1">
                              {defendant?.age &&
                                `${defendant.age} ${t("years")}`}
                              {defendant?.gender && ` • ${defendant.gender}`}
                              {defendant?.phone_number &&
                                ` • ${defendant.phone_number}`}
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {relatedCounts && relatedCounts?.length > 0 && (
                            <div>
                              <h4 className="text-sm font-semibold text-gray-900 mb-3">
                                {t("ioc_related_counts")}
                              </h4>
                              <div className="space-y-2">
                                {relatedCounts.map(
                                  (count: any, idx: number) => (
                                    <div
                                      key={idx}
                                      className="bg-gray-50 rounded-lg p-3"
                                    >
                                      <div className="font-medium text-sm text-gray-900">
                                        {t("ioc_count_number", {
                                          number: count.count_number,
                                        })}
                                      </div>
                                      <div className="text-xs text-gray-600 mt-1">
                                        <span className="font-medium">
                                          {t("crime_type")}:
                                        </span>{" "}
                                        {count.crime_type}
                                      </div>
                                      <div className="text-xs text-gray-600">
                                        <span className="font-medium">
                                          {t("level")}:
                                        </span>{" "}
                                        {count.crime_level}
                                      </div>
                                    </div>
                                  ),
                                )}
                              </div>
                            </div>
                          )}

                          {relatedWitnesses && relatedWitnesses?.length > 0 && (
                            <div>
                              <h4 className="text-sm font-semibold text-gray-900 mb-3">
                                {t("ioc_related_witnesses")}
                              </h4>
                              <div className="space-y-2">
                                {relatedWitnesses.map((w: any, idx: number) => (
                                  <div
                                    key={idx}
                                    className="bg-gray-50 rounded-lg p-3"
                                  >
                                    <div className="font-medium text-sm text-gray-900">
                                      {w?.witnessed_info?.first_name}{" "}
                                      {w?.witnessed_info?.middle_name}{" "}
                                      {w?.witnessed_info?.last_name}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                          <button
                            onClick={() => {
                              setSelectedDefendant(defendant);
                              setIsOpenDefendantDetail(true);
                            }}
                            className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                          >
                            {t("view_details_button")}
                          </button>

                          {(caseData?.status === "Pending" ||
                            caseData?.status === "Respond_Back_prosecutor" ||
                            caseData.is_case_updated_by_court) && (
                            <button
                              onClick={() => {
                                setSelectedData(caseLink);
                                setAction("edit");
                                setOpenForm(true);
                              }}
                              className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                            >
                              Edit
                            </button>
                          )}

                          {(caseData?.status === "Pending" ||
                            caseData?.status === "Respond_Back_prosecutor") && (
                            <button
                              onClick={() => {
                                setSelectedData(caseLink);
                                setOpenDelete(true);
                              }}
                              className="px-4 py-2 text-sm text-red-700 bg-red-100 rounded-lg hover:bg-red-200 transition-colors"
                            >
                              {t("ioc_delete")}
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              },
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      {isOpenDefendantDetail && (
        <SuspectDetailInfo
          suspect={selectedDefendant}
          isDefendant={true}
          isOpen={isOpenDefendantDetail}
          onClose={() => setIsOpenDefendantDetail(false)}
        />
      )}

      {openForm && caseRelatedParties && caseData && (
        <DefendantLinkForm
          caseData={caseData}
          action={action}
          selectedData={selectedData}
          caseRelatedParties={caseRelatedParties}
          onClose={(reload) => {
            setOpenForm(false);
            setSelectedData(undefined);
            if (reload) fetchData();
          }}
        />
      )}

      {openClosureForm && caseRelatedParties && caseData && (
        <SuspectClosureForm
          caseData={caseData}
          action={action}
          selectedData={selectedData}
          caseRelatedParties={caseRelatedParties}
          onClose={(reload) => {
            setOpenClosureForm(false);
            setSelectedData(undefined);
            if (reload) fetchData();
          }}
        />
      )}

      {selectedData && (
        <DeleteModal
          isOpen={isOpenDelete}
          onConfirm={() => {
            deleteCaseDefendantLink(selectedData?.id)
              .then(() => {
                showTooltip(t("successfully_deleted"), "success");
                fetchData();
                setOpenDelete(false);
                setSelectedData(undefined);
              })
              .catch((error) => {
                showTooltip(
                  error?.response?.data?.detail ||
                    errorHandler(error) ||
                    t("something_went_wrong_please_try_again"),
                );
                setOpenDelete(false);
                setSelectedData(undefined);
              });
          }}
          onCancel={() => {
            setOpenDelete(false);
            setSelectedData(undefined);
          }}
          title={t("ioc_delete_defendant_title")}
          message={t("ioc_delete_defendant_confirm", {
            name: `${selectedData?.defendant_info?.first_name || ""} ${
              selectedData?.defendant_info?.last_name || ""
            }`.trim(),
          })}
        />
      )}
    </div>
  );
};

export default DefendantList;
